<template>
  <div>
    <SlotPanel classes="min-h-full" id="seat-walk-in-tables">
      <ReservationGuestCount
        v-model="totalGuests"
        :minGuests="minGuests"
        :maxGuests="maxGuests"
        :disableCounter="isPayingInTerminal || isRetryingPaymentInTerminal"
      />

      <!--CHECKOUT INFO-->
      <ReservationPricing
        :coupons="coupons"
        :pricing="pricing"
        v-model:selectedCoupon="selectedCoupon"
      />

      <!--ERROR-->
      <ErrorDisplay
        v-if="ui.error && ui.error.message"
        v-bind="ui.error"
        id="error-message"
        class="mt-5"
      />

      <!--BUTTONS-->
      <SlotButton
        id="main-action-btn"
        :disabled="isMainActionBtnDisabled"
        classes="w-full mt-5 h-10"
        @click="mainActionBtnClick"
      >
        {{ mainActionBtnLabel }}
      </SlotButton>
      <div
        class="grid gap-4 mt-3"
        :class="isTerminalPaymentsAllowed ? 'grid-cols-3' : 'grid-cols-2'"
      >
        <ButtonDefault
          id="check-payment-status-btn"
          v-if="isTerminalPaymentsAllowed && reservation !== null"
          v-show="isPayingInTerminal && displayCheckPaymentStatusBtn"
          @click="fetchPaymentStatus"
          label="Check Status"
          classes="col-start-1 col-end-3"
          alt
        />
        <ButtonDefault
          v-if="isTerminalPaymentsAllowed"
          v-show="!isPayingInTerminal"
          id="seat-walk-in-btn"
          label="Seat "
          @click="$emit('seatWalkIn', totalGuests)"
          :disabled="!hasSelectedTables"
          alt
        />
        <ButtonDefault
          v-show="!isPayingInTerminal"
          id="add-reservation-btn"
          label="Reserve"
          @click="$emit('reserve')"
          :disabled="!hasSelectedTables"
          alt
        />
        <ButtonDefault
          id="cancel-seat-walk-in-btn"
          :classes="isTerminalPaymentsAllowed ? 'col-start-3 col-end-4' : 'col-start-2 col-end-3'"
          label="Cancel"
          @click="cancel"
          alt
        />
      </div>
    </SlotPanel>
    <StripeReaderSelect v-if="isTerminalPaymentsAllowed" class="mt-5" />
    <SimulatedReader v-if="showSimulatedReader" class="mt-5" />
  </div>
</template>

<script lang="ts">
import { useEventBus } from '@vueuse/core'
import * as _ from 'lodash-es'
import { mapState, mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import { readerKey } from '@/api/events'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import SlotButton from '@/common/components/atoms/SlotButton.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import SimulatedReader from '@/components/SimulatedReader.vue'
import StripeReaderSelect from '@/components/StripeReaderSelect.vue'
import { StoreReservation } from '@/models'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import ReservationGuestCount from '@/modules/reservation/components/molecules/ReservationGuestCount.vue'
import ReservationPricing from '@/modules/reservation/components/molecules/ReservationPricing.vue'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { OrderPricing, getPricing, seatExtraGuests, seatPartyAtTables } from '@/orders'
import { useSettingStore } from '@/stores/setting'
import { useTerminalStore } from '@/stores/terminal'
import { ErrorData, blankError, displayHandled, setError } from '@/utils/errors'
import { sum } from '@/utils/utils'

import {
  CouponRead,
  FeatureFlag,
  ReservationId,
  StripeReaderMessageRead,
  StripeReaderRead,
} from '@generated/types'

const COUPON_ZERO = {
  id: 0,
  percent_off: 0,
  name: 'None',
  terminal_eligible: true,
  max_redemptions: Infinity,
}

export default defineComponent({
  name: 'ReservationSellWalkIn',
  components: {
    ReservationPricing,
    ReservationGuestCount,
    SimulatedReader,
    ErrorDisplay,
    StripeReaderSelect,
    SlotPanel,
    SlotButton,
    ButtonDefault,
  },
  props: {
    error: {
      type: Object as PropType<ErrorData>,
      default: null,
    },
  },
  emits: {
    cancel: () => true,
    // eslint-disable-next-line no-unused-vars
    seatWalkIn: (totalOfGuests: number) => true,
    reserve: () => true,
    // eslint-disable-next-line no-unused-vars
    reservationPaid: (reservationId: ReservationId) => true,
  },
  data() {
    return {
      reservation: null as StoreReservation | null,
      totalGuests: 0,
      ui: { error: blankError() },
      displayCheckPaymentStatusBtn: false,
      coupons: [] as CouponRead[],
      selectedCoupon: COUPON_ZERO,
    }
  },
  computed: {
    ...mapStores(useSettingStore, useTerminalStore, useReservationStore),
    ...mapState(useSettingStore, ['currency']),
    ...mapState(useTerminalStore, [
      'isRetryingPaymentInTerminal',
      'isPayingInTerminal',
      'terminalPaymentState',
    ]),
    ...mapState(usePerformanceReservationStore, [
      'selectedPerformance',
      'selectedTables',
      'hasSelectedTables',
      'selectedTableIds',
    ]),
    minGuests(): number {
      return sum(this.selectedTables.map(table => table.minimum_guests))
    },
    maxGuests(): number {
      return sum(this.selectedTables.map(table => table.maximum_guests))
    },
    isMainActionBtnDisabled(): boolean {
      if (this.isTerminalPaymentsAllowed)
        return (
          this.isPayingInTerminal ||
          !this.hasSelectedTables ||
          this.totalGuests === 0 ||
          this.terminalReader === null ||
          this.terminalReader.status === 'offline'
        )
      return !this.hasSelectedTables
    },
    mainActionBtnLabel(): string {
      if (this.isRetryingPaymentInTerminal) return 'Retry Payment'
      if (this.isPayingInTerminal) return 'Waiting for Payment on Terminal'
      return this.isTerminalPaymentsAllowed ? 'Pay' : 'Seat Walk-In'
    },
    isTerminalPaymentsAllowed(): boolean {
      return this.settingStore.flagEnabled(FeatureFlag.TERMINAL)
    },
    terminalReader(): StripeReaderRead | null {
      return this.terminalStore.selectedReader
    },
    pricing(): OrderPricing {
      return getPricing(
        seatExtraGuests(
          seatPartyAtTables(this.selectedTables, this.totalGuests),
          this.totalGuests - this.maxGuests,
        ),
        [],
        this.totalGuests,
        this.settingStore.terminal_service_fee,
        this.settingStore.terminal_service_percentage,
        this.settingStore.tax_rate,
        this.settingStore.fee_tax_rate,
        this.selectedCoupon.percent_off,
      )
    },
    showSimulatedReader(): boolean {
      return this.isTerminalPaymentsAllowed && this.terminalStore.isSimulatedReader
    },
  },
  methods: {
    mainActionBtnClick(): void {
      if (this.isMainActionBtnDisabled) return
      if (!this.isTerminalPaymentsAllowed) this.$emit('seatWalkIn', this.totalGuests)
      else if (this.isRetryingPaymentInTerminal) this.retryPayment()
      else this.sellWalkIn()
    },
    async sellWalkIn(): Promise<void> {
      this.displayCheckPaymentStatusBtn = false
      if (!this.terminalReader) return
      try {
        this.ui.error = blankError()
        const { reservation, error } = await this.terminalStore.sellWalkIn(
          this.selectedPerformance!,
          this.selectedTableIds,
          this.totalGuests,
          this.pricing.total.toFixed(2),
          this.selectedCoupon.id === 0 ? undefined : this.selectedCoupon.id,
        )
        this.reservation = reservation
        if (error !== undefined) {
          setError(this.ui.error, error)
          await this.terminalStore.fetchReaders()
        } else {
          this.startPaymentStatusInterval()
        }
      } catch (error) {
        displayHandled(error, this.ui.error)
      }
    },
    async retryPayment(): Promise<void> {
      this.displayCheckPaymentStatusBtn = false
      if (!this.terminalReader || !this.reservation) return
      try {
        this.ui.error = blankError()
        await this.terminalStore.retrySellWalkIn(this.reservation.id)
        this.startPaymentStatusInterval()
      } catch (error) {
        displayHandled(error, this.ui.error)
      }
    },
    async onReaderEvent(event: StripeReaderMessageRead) {
      if (event.reader_id === this.terminalStore.selectedReader?.id && this.reservation !== null) {
        if (event.status == 'failed') {
          this.terminalStore.setTerminalPaymentStateRetry()
          this.ui.error.message = event.failure_message
          this.ui.error.code = event.failure_code
          this.displayCheckPaymentStatusBtn = false
        } else if (event.status === 'succeeded') {
          this.$emit('reservationPaid', this.reservation.id)
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'purchase',
              ecommerce: {
                currency: this.settingStore.currency,
                transaction_id: this.reservation?.id,
                value: this.pricing.total,
                items: [
                  {
                    item_id: this.selectedPerformance!.id,
                    item_name: this.selectedPerformance!.show.name,
                    item_variant: this.selectedTables.map(t => t.name).join(', '),
                    price: this.pricing.total,
                  },
                ],
              },
            })
          }
        }
      }
    },
    async cancel(): Promise<void> {
      this.$emit('cancel')
      await this.terminalStore.cancelReaderAction()
    },
    async fetchPaymentStatus() {
      if (!this.reservation) return
      await this.reservationStore.checkReservationPaymentStatus(this.reservation.id)
    },
    startPaymentStatusInterval() {
      setTimeout(() => {
        this.displayCheckPaymentStatusBtn = true
      }, 5000)
    },
    updateTotalGuests(value: number): void {
      if (value > 0) this.totalGuests = value
    },
    async loadCoupons() {
      const venueCoupons = await this.terminalStore.getCoupons()
      this.coupons = _.reject(_.filter(venueCoupons, { terminal_eligible: true }), {
        max_redemptions: 0,
      })
      this.coupons.forEach(coupon => {
        coupon.percent_off = Number(coupon.percent_off)
      })
      if (this.coupons.length > 0) {
        this.coupons.push(COUPON_ZERO)
        this.coupons = _.sortBy(this.coupons, ['percent_off'])
      }
    },
  },
  watch: {
    error() {
      this.ui.error = this.error
    },
  },
  async mounted() {
    this.totalGuests = this.selectedTables.some(t => t.standing_room)
      ? this.minGuests
      : this.maxGuests
    await this.settingStore.loading
    await this.loadCoupons()
    useEventBus(readerKey).on(this.onReaderEvent)
  },
  async unmounted() {
    useEventBus(readerKey).off(this.onReaderEvent)
    await this.terminalStore.cancelReaderAction()
    if (this.reservation) await this.reservationStore.deleteReservation(this.reservation.id)
  },
})
</script>
